import type { SchoolLevel, SubjectSlug } from '@jeraisonne/db';
import { trackAppEvent } from '@/lib/analytics/track';

/**
 * Émission d'événements de tracking pour la démo anonyme.
 *
 * Implémentation neutre : on tente d'appeler une fonction globale
 * `window.plausible` ou `window.dataLayer.push` si l'outil est branché.
 * Sinon on log (DEV uniquement) — la démo doit fonctionner sans tracking.
 */

export type DemoEvent =
  | { type: 'demo_started' }
  | { type: 'demo_level_selected'; level: SchoolLevel }
  | { type: 'demo_subject_selected'; subject: SubjectSlug }
  | { type: 'demo_completed' }
  | { type: 'demo_signup_clicked' };

type PlausibleFn = (event: string, options?: { props?: Record<string, string> }) => void;

type DataLayerEntry = { event: string; [key: string]: unknown };

type W = Window & {
  plausible?: PlausibleFn;
  dataLayer?: DataLayerEntry[];
};

/** Événements de démo comptés dans le récap journalier admin (migration 0523). */
const COUNTED: Partial<
  Record<DemoEvent['type'], 'demo_started' | 'demo_completed' | 'demo_signup_clicked'>
> = {
  demo_started: 'demo_started',
  demo_completed: 'demo_completed',
  demo_signup_clicked: 'demo_signup_clicked',
};

export function trackDemoEvent(evt: DemoEvent): void {
  if (typeof window === 'undefined') return;
  const counted = COUNTED[evt.type];
  if (counted) trackAppEvent(counted);
  const w = window as W;
  const props: Record<string, string> | undefined =
    evt.type === 'demo_level_selected'
      ? { level: evt.level }
      : evt.type === 'demo_subject_selected'
        ? { subject: evt.subject }
        : undefined;
  try {
    if (typeof w.plausible === 'function') {
      w.plausible(evt.type, props ? { props } : undefined);
    } else if (Array.isArray(w.dataLayer)) {
      w.dataLayer.push({ event: evt.type, ...(props ?? {}) });
    } else if (import.meta.env.DEV) {
      console.debug('[demo:track]', evt);
    }
  } catch {
    // tracking ne doit jamais casser l'UX
  }
}
